import { useEffect, useState } from "react";
import { Alert, Button, message, Popconfirm, Space, Tooltip } from "antd";
import { DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, WarningOutlined } from "@ant-design/icons";
import {
  BodyComponent,
  CreateButton,
  PaginationComponent,
  RefreshButton,
  TableComponent,
} from "@comps/components";
import { makeRequest } from "@utils/helpers";
import { deleteDocumentReq, getAllDocumentsReq } from "./requests";
import CreateDocument from "./components/CreateDocument";
import UpdateDocument from "./components/UpdateDocument";
import moment from "moment";

const IndexDocuments = (props) => {
  const [dataSource, setDataSource] = useState([]);
  const [loader, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    count: 0,
    per_page: 0,
    current_page: 0,
    total_pages: 0,
    links: {},
  });

  const canManage = !!props.canManage;

  const columns = [
    {
      key: "name",
      title: "Document Name",
      dataIndex: "name",
    },
    {
      key: "document_type_name",
      title: "Document Type",
      dataIndex: "document_type_name",
    },
    {
      key: "expiry_date",
      title: "Expiry Date",
      render: (record) => record.expiry_date ? moment(record.expiry_date).format("YYYY-MM-DD") : "",
    },
    {
      key: "attachment",
      title: "Attachment",
      render: (record) => (
        record.attachment?.url ? (
          <Button type="link" icon={ <DownloadOutlined /> } onClick={ () => openAttachment(record.attachment) }>
            { record.attachment.org_name || "Open file" }
          </Button>
        ) : "No file"
      ),
    },
    {
      key: "actions",
      title: "Actions",
      fixed: "right",
      render: (record) => (
        <Space>
          <Tooltip title="View document">
            <Button type="link" icon={ <EyeOutlined /> } onClick={ () => onView(record) } />
          </Tooltip>
          { canManage ? (
            <>
              <Tooltip title="Edit document">
                <Button type="link" icon={ <EditOutlined /> } onClick={ () => onEdit(record) } />
              </Tooltip>
              <Popconfirm title="Are you sure?" icon={ <WarningOutlined /> } onConfirm={ () => onDelete(record) }>
                <Tooltip title="Delete document">
                  <Button type="link" danger icon={ <DeleteOutlined /> } />
                </Tooltip>
              </Popconfirm>
            </>
          ) : null }
        </Space>
      ),
    },
  ];

  useEffect(() => {
    getAllData();
    // eslint-disable-next-line
  }, [currentPage, props.employmentId]);

  const getAllData = () => {
    const payload = {
      page: currentPage,
      filters: props.filters,
      employmentId: props.employmentId,
    };
    makeRequest(setLoader, getAllDocumentsReq, payload, onSuccess, onError);
  };

  const onSuccess = (response) => {
    setPagination(response.my_documents);
    setDataSource(response.my_documents.data || []);
  };

  const onCreate = () => {
    setChildComponent(
      <CreateDocument
        employmentId={ props.employmentId }
        filters={ props.filters }
        onCompleted={ onCompleted }
        onSuccess={ onSuccess }
      />
    );
  };

  const onEdit = (record) => {
    setChildComponent(
      <UpdateDocument
        employmentId={ props.employmentId }
        filters={ props.filters }
        record={ record }
        disabled={ false }
        onCompleted={ onCompleted }
      />
    );
  };

  const onView = (record) => {
    setChildComponent(
      <UpdateDocument
        employmentId={ props.employmentId }
        filters={ props.filters }
        record={ record }
        disabled
        onCompleted={ onCompleted }
      />
    );
  };

  const onDelete = (record) => {
    makeRequest(setLoader, deleteDocumentReq, { id: record.id, employmentId: props.employmentId }, onDeleted, onError);
  };

  const onDeleted = (data) => {
    message.success("Document deleted successfully.");
    onSuccess(data);
  };

  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Documents could not be loaded.");
  };

  const onCompleted = (data) => {
    if (data?.length) {
      setDataSource(data);
    }
    setChildComponent(null);
  };

  const handleTableChange = (page, fil, sorter) => {
    setPagination({
      ...pagination,
      current: page.current,
      pageSize: page.pageSize,
      sortName: sorter.field || "id",
      sortType: sorter.order === "ascend" ? "asc" : "desc",
    });
  };

  return (
    <>
      { childComponent }
      <div className="da-px-32 da-py-16" style={ { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 } }>
        <h3 style={ { margin: 0 } }>Documents</h3>
        <Space>
          <RefreshButton onClick={ getAllData } />
          { canManage ? <CreateButton onClick={ onCreate } text="Upload Document" /> : null }
        </Space>
      </div>
      <BodyComponent>
        { !loader && !dataSource.length ? (
          <Alert
            className="da-mb-16"
            type="info"
            showIcon
            message={ canManage ? "No documents uploaded yet." : "No documents are available in your profile yet." }
            description={ canManage ? "Use Upload Document to add employee letters, contracts, or other HR documents." : undefined }
          />
        ) : null }
        <TableComponent
          modkey={ props.modkey }
          loader={ loader }
          columns={ columns }
          dataSource={ dataSource }
          pagination={ { ...pagination, total: 0 } }
          onChange={ handleTableChange }
        />
      </BodyComponent>
      <PaginationComponent pagination={ pagination } setCurrentPage={ setCurrentPage } currentPage={ currentPage } display />
    </>
  );
};

const openAttachment = (attachment) => {
  if (attachment?.url) {
    window.open(attachment.url, "_blank");
  }
};

export default IndexDocuments;
